import React, { useMemo, useState, useRef, useEffect } from 'react'
import {
  Col,
  message,
  Popconfirm,
  Row,
  Tag,
  Tooltip,
  Modal,
  Timeline,
  Upload,
  Button,
} from 'antd'
import {
  CommentHidden,
  FlexBetweenBox,
  WorkCommentBox,
  UploadFileListWrapper,
} from '../../../../components/CommentComponent/CommentComponentStyled'
import {
  DeleteTwoTone,
  QuestionCircleOutlined,
  EditOutlined,
  HistoryOutlined,
  PaperClipOutlined,
} from '@ant-design/icons'
import utils from '../../../../utils'
import RichText from '../../../../components/RichEditor/RichText'
import FileItem from '../../../../components/Common/FileItemComponent/FileItem'
import date_format from '../../../../date_format'
import loadingAnimationStore from '../../../../stores/loadingAnimationStore'
import workStore from '../../../../stores/workStore'
import { hozDividerNoMarginSide } from '../../../../ui_constants'
import { inject, observer } from 'mobx-react'
import RichEditor from '../../../../components/RichEditor/RichEditor'
import UploadFileItem from '../../../../components/Common/FileItemComponent/UploadFileItem'
import fileStore from '../../../../stores/fileStore'
import { apiUrl } from '../../../../config'

const resolveFileId = file =>
  file?.file_id ||
  file?.fileId ||
  file?.response?.file_id ||
  file?.response?.fileId

const resolveFileName = file =>
  file?.file_name ||
  file?.fileName ||
  file?.name ||
  file?.response?.file_name ||
  file?.response?.fileName ||
  ''

const WorkCommentList = props => {
  const {
    commentList,
    setCommentList,
    code,
    authenticationStore,
    onEditingChange,
  } = props
  const { currentUser } = authenticationStore

  const [editingCommentId, setEditingCommentId] = useState(null)
  const [editingContent, setEditingContent] = useState('')
  const [editingFileList, setEditingFileList] = useState([])
  const EDIT_EDITOR_REF = useRef(null)

  const [historyVisible, setHistoryVisible] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyItems, setHistoryItems] = useState([])
  const [historyTitle, setHistoryTitle] = useState('Lịch sử chỉnh sửa ý kiến')

  useEffect(() => {
    onEditingChange?.(!!editingCommentId)
  }, [editingCommentId, onEditingChange])

  const handleDeleteComment = async (comment_id, index) => {
    try {
      loadingAnimationStore.setShowSpinInline(true)
      await workStore.deleteCommentWork(code, comment_id)
      const newCommentList = [...commentList]
      newCommentList[index] = {
        ...newCommentList[index],
        deleted: true,
      }
      setCommentList(newCommentList)
      props.handleShowDeletedComments(newCommentList, props.isShowDelComments)
      message.success('Xóa ý kiến thành công!')
    } catch (err) {
      console.log(err)
      message.error(err?.vi || 'Đã có lỗi xảy ra')
    } finally {
      loadingAnimationStore.setShowSpinInline(false)
    }
  }

  const handleStartEdit = comment => {
    setEditingCommentId(comment.comment_id)
    setEditingContent(comment.content)
    setEditingFileList(
      comment.files
        ? comment.files.map(f => ({
            uid: f.file_id,
            file_id: f.file_id,
            file_name: f.file_name,
            name: f.file_name,
            url: `${apiUrl}/api/v1/upload/attachments/${f.file_id}`,
            status: 'done',
          }))
        : []
    )
  }

  const handleCancelEdit = () => {
    setEditingCommentId(null)
    setEditingContent('')
    setEditingFileList([])
  }

  const handleSaveEdit = async comment => {
    const stillUploading = editingFileList.some(
      file => file.status === 'uploading'
    )
    if (stillUploading) {
      message.warning('Vui lòng đợi file tải lên xong')
      return
    }

    let editedContent = EDIT_EDITOR_REF.current.editor.getData()
    const fileIds = editingFileList.map(resolveFileId).filter(Boolean)

    try {
      loadingAnimationStore.setShowSpinInline(true)
      await workStore.updateCommentWork(code, comment.comment_id, {
        content: editedContent,
        file_ids: fileIds,
      })
      await workStore.getCommentWorkList(code)
      setEditingCommentId(null)
      message.success('Cập nhật ý kiến thành công!')
    } catch (err) {
      console.error(err)
      message.error(err?.vi || 'Cập nhật ý kiến thất bại!')
    } finally {
      loadingAnimationStore.setShowSpinInline(false)
    }
  }

  const handleShowHistory = async comment => {
    setHistoryTitle(
      `Lịch sử chỉnh sửa — ${utils.getNameInCapitalize(comment.name_uppercase)}`
    )
    setHistoryVisible(true)
    setHistoryLoading(true)
    setHistoryItems([])
    try {
      const response = await workStore.getCommentWorkHistory(
        code,
        comment.comment_id
      )
      setHistoryItems(response?.data || [])
    } catch (err) {
      console.error(err)
      message.error('Không tải được lịch sử chỉnh sửa!')
    } finally {
      setHistoryLoading(false)
    }
  }

  const handleRemoveFileFromUploadList = fileUID => {
    const newFileList = editingFileList.filter(file => file.uid !== fileUID)
    setEditingFileList(newFileList)
  }

  const isCommentEdited = comment => {
    if (!comment.updated_at || !comment.created_at) return false
    const diff = Math.abs(
      Date.parse(comment.updated_at) - Date.parse(comment.created_at)
    )
    return diff > 1000
  }

  const renderCommentList = useMemo(() => {
    if (commentList.length === 0) {
      return
    }
    return (
      commentList &&
      commentList.map((comment, index) => {
        const isOwner = comment.user_name === currentUser?.username
        const isEditing = comment.comment_id === editingCommentId

        return (
          <CommentHidden key={comment.comment_id}>
            {!comment.isHidden && (
              <WorkCommentBox
                key={comment.comment_id}
                deleted={comment.deleted}>
                {isEditing ? (
                  <div
                    style={{
                      padding: '8px 0',
                      color: '#000000',
                    }}
                    className="work-comment-edit-editor">
                    <div
                      style={{
                        fontWeight: 600,
                        marginBottom: 8,
                        fontSize: '13px',
                        color: '#000000',
                      }}>
                      Chỉnh sửa ý kiến:
                    </div>
                    <RichEditor
                      key={`edit-${comment.comment_id}`}
                      EDITOR_REF={EDIT_EDITOR_REF}
                      editorContent={editingContent || ''}
                      height={100}
                    />
                    {editingFileList.length !== 0 && (
                      <UploadFileListWrapper style={{ marginTop: 8 }}>
                        {editingFileList.map(file => (
                          <UploadFileItem
                            key={file.uid}
                            file_id={file.file_id}
                            file_name={file.name || file.file_name}
                            file_type={utils.getExtensionFile(
                              file.name || file.file_name
                            )}
                            handleRemoveFileFromUploadList={() =>
                              handleRemoveFileFromUploadList(file.uid)
                            }
                          />
                        ))}
                      </UploadFileListWrapper>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: 8,
                      }}>
                      <Upload
                        multiple
                        showUploadList={false}
                        fileList={editingFileList}
                        customRequest={async options => {
                          const { file, onSuccess, onError } = options
                          const formData = new FormData()
                          formData.append('file', file)
                          try {
                            const res = await fileStore.uploadFile(formData)
                            if (res && res.data) {
                              file.file_id = res.data.file_id
                              file.file_name = res.data.file_name
                              file.url = `${apiUrl}/api/v1/upload/attachments/${res.data.file_id}`
                              onSuccess(res.data, file)
                            }
                          } catch (e) {
                            console.error(e)
                            onError(e)
                            message.error('Upload thất bại')
                          }
                        }}
                        onChange={info => {
                          const newFileList = info.fileList.map(file => {
                            const prev = editingFileList.find(
                              item => item.uid === file.uid
                            )
                            const fileId =
                              resolveFileId(file) || resolveFileId(prev)
                            const fileName =
                              resolveFileName(file) || resolveFileName(prev)
                            return {
                              ...file,
                              ...prev,
                              ...file,
                              file_id: fileId,
                              file_name: fileName,
                              name: fileName || file.name,
                              url: fileId
                                ? `${apiUrl}/api/v1/upload/attachments/${fileId}`
                                : file.url,
                            }
                          })
                          setEditingFileList(newFileList)
                        }}>
                        <Button icon={<PaperClipOutlined />}>
                          Tải lên file
                        </Button>
                      </Upload>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button danger onClick={handleCancelEdit}>
                          Hủy bỏ
                        </Button>
                        <Button
                          type="primary"
                          onClick={() => handleSaveEdit(comment)}>
                          Lưu ý kiến
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <Row align={'middle'} style={{ marginBottom: 0 }}>
                      <Col flex={'auto'}>
                        <b className={'name-create'}>
                          {utils.getNameInCapitalize(comment.name_uppercase)}
                        </b>
                      </Col>
                      <Col>
                        <p className={'time'}>
                          {date_format.renderTime(comment.created_at)}
                          {isCommentEdited(comment) && !comment.deleted && (
                            <span
                              style={{
                                fontSize: 10,
                                fontStyle: 'italic',
                                color: '#8c8c8c',
                                marginLeft: 6,
                              }}>
                              (đã sửa)
                            </span>
                          )}
                        </p>
                      </Col>
                    </Row>

                    <Row style={{ marginBottom: 2 }} wrap={false} gutter={8}>
                      <Col flex={'auto'} className={'content'}>
                        <RichText htmlText={comment.content.trim()} />
                      </Col>
                      <Col>
                        {!comment.deleted ? (
                          <div
                            style={{
                              display: 'flex',
                              gap: '8px',
                              alignItems: 'center',
                            }}>
                            {isOwner && (
                              <Tooltip title={'Sửa ý kiến'}>
                                <EditOutlined
                                  style={{
                                    color: '#1890ff',
                                    cursor: 'pointer',
                                    fontSize: 13,
                                  }}
                                  onClick={() => handleStartEdit(comment)}
                                />
                              </Tooltip>
                            )}
                            <Tooltip title={'Lịch sử chỉnh sửa'}>
                              <HistoryOutlined
                                style={{
                                  color: '#8c8c8c',
                                  cursor: 'pointer',
                                  fontSize: 13,
                                }}
                                onClick={() => handleShowHistory(comment)}
                              />
                            </Tooltip>
                            {isOwner && (
                              <Popconfirm
                                title={'Bạn có muốn xoá ý kiến này'}
                                okType={'danger'}
                                okText={'Xoá'}
                                onConfirm={() =>
                                  handleDeleteComment(comment.comment_id, index)
                                }
                                placement={'topRight'}
                                cancelText={'Không'}
                                icon={
                                  <QuestionCircleOutlined
                                    style={{ color: '#fc0000' }}
                                  />
                                }>
                                <Tooltip title={'Xóa ý kiến'}>
                                  <DeleteTwoTone
                                    twoToneColor={'#fc0000'}
                                    style={{
                                      cursor: 'pointer',
                                      fontSize: 13,
                                    }}
                                  />
                                </Tooltip>
                              </Popconfirm>
                            )}
                          </div>
                        ) : (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              alignItems: 'center',
                            }}>
                            <Tag
                              style={{
                                marginRight: 0,
                                fontSize: 10,
                                borderRadius: '10px',
                              }}
                              color={'red'}>
                              Đã xóa
                            </Tag>
                          </div>
                        )}
                      </Col>
                    </Row>

                    <FlexBetweenBox>
                      {comment.files
                        ? comment.files.map(file => (
                            <FileItem
                              key={file.file_id}
                              file_id={file.file_id}
                              file_name={file.file_name}
                              file_type={utils.getExtensionFile(file.file_name)}
                              deleted={comment.deleted}
                            />
                          ))
                        : []}
                    </FlexBetweenBox>
                  </>
                )}

                {comment.files && <div style={{ height: 12 }} />}

                <div style={hozDividerNoMarginSide} />
              </WorkCommentBox>
            )}
          </CommentHidden>
        )
      })
    )
  }, [
    commentList,
    editingCommentId,
    editingContent,
    editingFileList,
    currentUser,
  ])

  return (
    <div>
      {renderCommentList}

      <Modal
        title={historyTitle}
        visible={historyVisible}
        onCancel={() => setHistoryVisible(false)}
        footer={[
          <Button key="close" onClick={() => setHistoryVisible(false)}>
            Đóng
          </Button>,
        ]}
        width={600}>
        <div
          style={{ maxHeight: '400px', overflowY: 'auto', padding: '8px 4px' }}>
          {historyLoading && (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              Đang tải lịch sử...
            </div>
          )}
          {!historyLoading && historyItems.length === 0 && (
            <div
              style={{
                fontStyle: 'italic',
                textAlign: 'center',
                padding: '16px 0',
                color: '#aaa',
              }}>
              Chưa có lịch sử chỉnh sửa nào cho ý kiến này.
            </div>
          )}
          {!historyLoading && historyItems.length > 0 && (
            <Timeline style={{ margin: '16px 0 0' }}>
              {historyItems.map(item => {
                const filesBefore = (item.files_before || [])
                  .map(f => ({
                    file_id: resolveFileId(f),
                    file_name: resolveFileName(f),
                  }))
                  .filter(f => f.file_id || f.file_name)
                const filesAfter = (item.files_after || [])
                  .map(f => ({
                    file_id: resolveFileId(f),
                    file_name: resolveFileName(f),
                  }))
                  .filter(f => f.file_id || f.file_name)
                const isCreate =
                  !item.content_before && filesBefore.length === 0

                return (
                  <Timeline.Item key={item.id} color="blue">
                    <div style={{ marginBottom: 8 }}>
                      <strong>{isCreate ? 'Đăng ý kiến' : 'Cập nhật'}</strong>{' '}
                      bởi{' '}
                      <span>
                        {utils.getNameInCapitalize(item.name_uppercase)}
                      </span>{' '}
                      vào lúc{' '}
                      <span style={{ fontStyle: 'italic', color: '#555' }}>
                        {date_format.HH_mm_DD_MM_YYYY(item.edited_at)}
                      </span>
                    </div>
                    <div
                      style={{
                        border: '1px solid #f0f0f0',
                        borderRadius: '4px',
                        background: '#fafafa',
                        padding: '8px 12px',
                        fontSize: '13px',
                      }}>
                      {item.content_before && (
                        <div style={{ marginBottom: 8 }}>
                          <div
                            style={{
                              fontWeight: 600,
                              color: '#ff4d4f',
                              marginBottom: 2,
                            }}>
                            Nội dung cũ:
                          </div>
                          <RichText htmlText={item.content_before} />
                        </div>
                      )}
                      {item.content_after && (
                        <div style={{ marginBottom: 8 }}>
                          <div
                            style={{
                              fontWeight: 600,
                              color: '#52c41a',
                              marginBottom: 2,
                            }}>
                            {isCreate ? 'Nội dung:' : 'Nội dung mới:'}
                          </div>
                          <RichText htmlText={item.content_after} />
                        </div>
                      )}
                      {(filesBefore.length > 0 || filesAfter.length > 0) && (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                            marginTop: 10,
                            paddingTop: 8,
                            borderTop: '1px dashed #e8e8e8',
                          }}>
                          {filesBefore.length > 0 && (
                            <div>
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: '11px',
                                  color: '#ff4d4f',
                                }}>
                                Tệp đính kèm cũ:
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 8,
                                  marginTop: 4,
                                }}>
                                {filesBefore.map((f, idx) => (
                                  <FileItem
                                    key={`${item.id}-bef-${idx}`}
                                    file_id={f.file_id}
                                    file_name={f.file_name}
                                    file_type={utils.getExtensionFile(
                                      f.file_name
                                    )}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                          {filesAfter.length > 0 && (
                            <div style={{ marginTop: 4 }}>
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: '11px',
                                  color: '#52c41a',
                                }}>
                                {isCreate
                                  ? 'Tệp đính kèm:'
                                  : 'Tệp đính kèm mới:'}
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 8,
                                  marginTop: 4,
                                }}>
                                {filesAfter.map((f, idx) => (
                                  <FileItem
                                    key={`${item.id}-aft-${idx}`}
                                    file_id={f.file_id}
                                    file_name={f.file_name}
                                    file_type={utils.getExtensionFile(
                                      f.file_name
                                    )}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                          {!isCreate &&
                            filesBefore.length > 0 &&
                            filesAfter.length === 0 && (
                              <div
                                style={{
                                  fontSize: '11px',
                                  fontStyle: 'italic',
                                  color: '#8c8c8c',
                                }}>
                                Đã xóa hết tệp đính kèm
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                  </Timeline.Item>
                )
              })}
            </Timeline>
          )}
        </div>
      </Modal>
    </div>
  )
}

WorkCommentList.propTypes = {}

export default inject('authenticationStore')(observer(WorkCommentList))
